import type { Category } from '@shopgnd/shared';
import { getCategoryPath } from '@/lib/categoryTree';

/** Match shop `?category=` values by id, slug, or legacy `cat-*` ids. */
export function findCategoryByParam(
  categories: Category[],
  param: string | undefined,
): Category | undefined {
  if (!param?.trim()) return undefined;
  const norm = param.trim();
  const direct = categories.find((c) => c.id === norm || c.slug === norm);
  if (direct) return direct;

  const withoutCatPrefix = norm.replace(/^cat-/, '');
  const legacy = categories.find(
    (c) =>
      c.slug === withoutCatPrefix ||
      c.id === norm ||
      c.id === `cat-${withoutCatPrefix}` ||
      c.slug === norm.replace(/^cat-/, ''),
  );
  if (legacy) return legacy;

  const lower = norm.toLowerCase();
  return categories.find(
    (c) => c.id.toLowerCase() === lower || c.slug.toLowerCase() === lower,
  );
}

/** Readable label when the category record is not in the loaded list. */
export function humanizeCategoryParam(param: string): string {
  const core = param.trim().replace(/^cat-/, '');
  return core
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Preferred `?category=` query value (slug when available). */
export function shopCategoryQueryValue(category: Category): string {
  return category.slug?.trim() || category.id;
}

export function shopCategoryHref(category: Category): string {
  return `/grenada/shop/?category=${encodeURIComponent(shopCategoryQueryValue(category))}`;
}

export function getCategoryName(categories: Category[], categoryId: string | undefined): string {
  if (!categoryId) return 'Uncategorized';
  const cat = findCategoryByParam(categories, categoryId);
  if (cat) return cat.name;
  return humanizeCategoryParam(categoryId);
}

/** Full breadcrumb label, e.g. "Electronics › Phones". */
export function getCategoryLabel(categories: Category[], categoryId: string | undefined): string {
  if (!categoryId) return 'Uncategorized';
  const cat = findCategoryByParam(categories, categoryId);
  const path = getCategoryPath(categories, cat?.id ?? categoryId);
  if (path.length > 0) return path.map((c) => c.name).join(' › ');
  return humanizeCategoryParam(categoryId);
}
